import React from "react";
import * as Icons from "lucide-react";
import AuthImage from "@/components/AuthImage";

const normUrl = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : u);
const stripProto = (u) => (u ? u.replace(/^https?:\/\//i, "") : u);

const ContactRow = ({ icon, value, href, testid }) => {
  if (!value) return null;
  const Icon = Icons[icon] || Icons.Circle;
  const inner = (
    <div className="flex items-center gap-3 text-sm text-gray-700 min-w-0">
      <span className="w-7 h-7 rounded-lg bg-[#1B4332]/[0.08] flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#1B4332]" />
      </span>
      <span className="truncate" data-testid={testid}>{value}</span>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="block hover:opacity-70 transition-opacity">{inner}</a>
  ) : inner;
};

// Reusable digital business card visual. Renova Eco Systems branding.
export default function BusinessCard({ name, email, role, profile = {}, className = "" }) {
  const company = profile.company || "Renova Eco Systems";
  const initials = (name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const hasLogo = !!profile.logo_file_id;
  const subline = [profile.department, company].filter(Boolean).join(" · ");

  return (
    <div
      data-testid="business-card"
      className={`relative overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_-12px_rgba(27,67,50,0.25)] ${className}`}
    >
      {/* Brand header */}
      <div className="relative h-[92px] bg-[#1B4332]">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_88%_-30%,#ffffff,transparent_55%)]" />
        <div className="absolute inset-x-5 top-4 flex items-start justify-between">
          <div className="bg-white rounded-lg px-2.5 py-1.5 shadow-sm flex items-center h-9 max-w-[65%]">
            {hasLogo ? (
              <AuthImage
                fileId={profile.logo_file_id}
                alt={company}
                className="h-5 max-w-[140px] object-contain"
                fallback={<span className="text-[11px] font-bold text-[#1B4332] tracking-tight truncate" data-testid="card-company">{company}</span>}
              />
            ) : (
              <span className="text-[11px] font-bold text-[#1B4332] tracking-tight truncate" data-testid="card-company">{company}</span>
            )}
          </div>
          {role === "admin" && (
            <span className="text-[10px] uppercase tracking-[0.12em] text-white/60 font-semibold mt-1">Admin</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-5 pb-5">
        <div className="-mt-12 mb-3 relative z-10">
          <div className="w-[88px] h-[88px] rounded-2xl ring-4 ring-white bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex items-center justify-center shadow-md">
            <AuthImage
              fileId={profile.avatar_file_id}
              alt={name}
              className="w-full h-full object-cover"
              fallback={<span className="text-2xl font-extrabold text-[#1B4332]">{initials}</span>}
            />
          </div>
        </div>

        <h3 className="font-heading text-xl font-extrabold tracking-tight leading-tight text-[#0A0A0A]" data-testid="card-name">{name}</h3>
        {profile.title && <p className="text-sm font-semibold text-[#1B4332] mt-0.5" data-testid="card-title">{profile.title}</p>}
        {subline && <p className="text-xs text-gray-400 mt-0.5">{subline}</p>}

        <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
          <ContactRow icon="Mail" value={profile.work_email || email} href={`mailto:${profile.work_email || email}`} testid="card-email" />
          <ContactRow icon="Phone" value={profile.phone} href={`tel:${profile.phone}`} testid="card-phone" />
          <ContactRow icon="Globe" value={stripProto(profile.website)} href={normUrl(profile.website)} testid="card-website" />
          <ContactRow icon="Linkedin" value={stripProto(profile.linkedin)} href={normUrl(profile.linkedin)} testid="card-linkedin" />
          <ContactRow icon="MapPin" value={profile.address} testid="card-address" />
        </div>
      </div>
    </div>
  );
}
